import { resolverSaldo } from "../cobro/resolverSaldo";
import { esDeclaracionIngreso, esNotaCredito } from "../dte";
import { construirExclusionNcAnulaError, netearNcSinRefPago } from "../ncAnulaError";
import { buildSaldoInicialEntry, FECHA_CORTE } from "./saldoInicial";

// ─── Tolerancia de redondeo ──────────────────────────────────────────────────
// Una factura que, TRAS recibir pago (o NC/MBF), queda con una miga de saldo ≤ este umbral se
// considera saldada al 100% y sale de "Por pagar"/"parcial". Caso real: 3 pagos de 388.733
// (=1.166.199) contra una factura de 1.166.200 → residuo de $1 (INNOVA folio 2698).
// Guarda IMPRESCINDIBLE: SOLO se aplica si hubo pago (débito − saldo > 0). Una factura nunca
// pagada (saldo == débito) NO se barre, por chica que sea. El monto_total real de la factura
// NO se toca (no se falsifica plata): solo deja de proyectarse la miga como pendiente.
// El umbral está en MONEDA LOCAL del país (CLP/PEN). El valor efectivo por-ledger se deriva con
// toDisplayCurrency(TOLERANCIA_REDONDEO_LOCAL, monedaPrincipal): en un ledger expresado en moneda
// local vale ~10; en un ledger USD colapsa a ~0 → inerte. NOTA: un residuo genuino en USD
// necesitaría su propio umbral EN USD — fuera de alcance hoy (no hay residuos USD sub-umbral).
export const TOLERANCIA_REDONDEO_LOCAL = 10;

/**
 * Construye el ledger contable de un proveedor (movimientos + saldo acumulado).
 *
 * Convención de signo del saldo acumulado:
 *   - saldo > 0  → el proveedor nos debe (saldo a nuestro favor)
 *   - saldo < 0  → le debemos al proveedor (deuda nuestra)
 *
 * Débitos (suben el saldo a favor nuestro):
 *   - Facturas SII recibidas (origen "SII"). NC (tipo_dte=61) entra como CRÉDITO POSITIVO
 *     (bucket "nota_credito"), no como débito negativo — reduce el saldo igual pero contable
 *     y visualmente queda en el haber. Ver regla 10.42.
 *   - Facturas externas no anuladas (origen "EXT") — moneda nativa.
 *   - Cobros a cuenta del proveedor — `origen: null` (importante para el filtro de
 *     "Total facturado" en EstadoCuenta, que excluye este origen).
 *     movimientos_caja tipo='cobro' entidad_tipo='proveedor'. **Son débitos** porque
 *     representan transferencias entrantes DESDE el proveedor HACIA la empresa
 *     (préstamos del socio, devoluciones de anticipos previos, refacturaciones).
 *     Aumentan el saldo a favor nuestro: si previamente pagamos de más al proveedor
 *     (saldo<0), una devolución suya cancela ese sobrepago. Si no había deuda
 *     previa, este cobro se convierte en saldo positivo (el proveedor nos quedó
 *     debiendo esa plata). NUNCA confundir con "el proveedor está pagando una
 *     deuda con nosotros" — eso no aplica en una cuenta corriente de proveedor.
 *
 * Créditos (suben la deuda con el proveedor):
 *   - Pagos al proveedor: movimientos_caja tipo='pago' entidad_tipo='proveedor'.
 *     Concepto = "Pago a cuenta" si categoria_pago='pago_a_cuenta', si no "Pago".
 *
 * Saldo inicial opcional al 2026-01-01 entra como entry sintética (origen "INI").
 *
 * Filtrado por entidad: SIEMPRE por proveedor_id numérico, nunca por nombre.
 *
 * `saldosPorFactura` se construye en el mismo loop aplicando FIFO de pagos
 * (entries con `origen===null` y `creditoDisplay>0`) sobre las facturas SII/EXT
 * registradas hasta ese punto cronológico. Una factura cuyo saldoDisplay queda ≤ 0.01
 * se filtra al final. NCs (debito=0, credito>0) no se registran. Cobros a cuenta y saldo
 * inicial no participan ni de la registración ni de la aplicación FIFO.
 *
 * @param {object} args
 * @param {number} args.provId                    ID numérico del proveedor.
 * @param {Array}  args.siiRecibidas              Facturas SII recibidas del país.
 * @param {Array}  args.facturasExternas          Facturas externas del país.
 * @param {Array}  args.movimientosCaja           Movimientos de caja del país desde FECHA_CORTE.
 * @param {object} [args.saldoInicial]            Saldo inicial de la entidad (o null).
 * @param {string} args.monedaPrincipal           Moneda local del país ("CLP" o "PEN").
 * @param {Function} args.toDisplayCurrency       (monto, monedaOrigen, fecha) → monto en moneda display.
 * @param {Array}  [args.vinculacionesDirectas]   Filas de movimiento_bancario_facturas (MBF), sin filtrar
 *   por proveedor. Una factura con vinculación directa a un pago vía MBF se considera 100% saldada
 *   (no entra a saldosPorFactura), y el pago vinculado NO participa del FIFO. Evita residuos fantasma
 *   por drift de TC cuando un pago en moneda local salda 1:1 una factura en otra moneda. Default: [].
 * @param {Set|Array} [args.facturasComisionFactoring] IDs de facturas SII recibidas que son comisión de
 *   factoring vinculada a una operación (se descuentan del giro, no son deuda real). Se excluyen del
 *   ledger y del FIFO. Default: Set vacío.
 * @param {boolean} [args.excluirIgnoradas] Si es true, las facturas SII con estado_vinculacion='ignorada'
 *   NO entran al ledger (ni débito ni FIFO ni residual_corte). Opt-in (default false): el ER y EstadoCuenta
 *   NO lo pasan (para ellos 'ignorada' es un estado de CAJA, no de costo). Lo activan getSaldosPorFacturaProveedor
 *   / getSaldosRecibidasPorFactura para igualar el criterio de getFacturasRecibidas(estado_not:'ignorada'),
 *   que es la fuente del reload en cargarDatosFuturos → patch scoped y reload dan el MISMO resultado.
 * @returns {{ entries: Array, saldosPorFactura: Map<string,{saldoDisplay,factura}> }}
 *   - `entries`: ordenadas DESC por fecha, con debitoDisplay/creditoDisplay/saldo.
 *   - `saldosPorFactura`: keyed por `"${origen}:${facturaId}"` — composite porque SII/EXT
 *     son tablas con IDs independientes que pueden colisionar en raw id. Solo facturas con
 *     saldoDisplay > 0.01 (deuda restante). `saldoDisplay` está en la moneda que produzca
 *     `toDisplayCurrency` (el consumidor sabe qué moneda pasó).
 */
export const construirLedgerProveedor = ({
  provId,
  siiRecibidas,
  facturasExternas,
  movimientosCaja,
  honorarios = [],
  saldoInicial,
  monedaPrincipal,
  toDisplayCurrency,
  vinculacionesDirectas = [],
  excluirProvIds = new Set(),
  facturasComisionFactoring = new Set(),
  excluirIgnoradas = false,
  // R3.2 — cutover del eje pago (regla 10.190): si el consumidor pasa el contexto del libro
  // (`buildContextoLibro(aplicaciones_saldo)`), la SALDADA/SALDO por factura la determina
  // `resolverSaldo` sobre el libro, NO la amortización FIFO. Sin ctxLibro se conserva el FIFO
  // (baseline del dual-run / fallback). RESIDUO e INI (saldo a cuenta / saldo inicial) NO cambian.
  ctxLibro = null,
  // Fase 9b (10.245): filas de `rendiciones_anticipo` del país (sin filtrar por proveedor — el
  // filtro por `provId` es interno, mismo patrón que `movimientosCaja`/`siiRecibidas`). Default []
  // → comportamiento IDÉNTICO al actual para todo caller que no las pase.
  rendicionesAnticipo = [],
}) => {
  if (!provId) return [];
  // Comisiones de factoring (Xepelin/X Capital, Chita, Fingo/Santander) vinculadas a una
  // operación: se descuentan del giro en origen, NO son deuda real. Se excluyen del ledger
  // (no entran como débito ni al FIFO). Acepta Set o array. Ver operaciones_factoring_comisiones.
  const setComisionFactoring =
    facturasComisionFactoring instanceof Set ? facturasComisionFactoring : new Set(facturasComisionFactoring || []);
  // Proveedor-edificio (su id está en edificios.proveedor_id): su deuda se mide solo por la
  // comisión de sus edificios (getEdificiosSaldoPendiente), no como obligación de proveedor.
  // Excluirlo acá evita el doble conteo en TODO consumidor del ledger (FlujoCajaV2, EECC).
  // Comparación por ID (regla 8). Acepta Set o array.
  const setExcluir = excluirProvIds instanceof Set ? excluirProvIds : new Set(excluirProvIds || []);
  if (setExcluir.has(provId)) return [];

  // Par NC↔factura 'anula_error' (regla 10.222): la anulación cancela AMBOS documentos. Ni la
  // factura anulada (como débito fantasma) ni su NC (como crédito genérico que se aplicaría a otra
  // factura por FIFO) deben entrar al ledger — igual que en costosER.js (regla 10.221). Se excluyen
  // por ID, en todos los filtros sobre `siiRecibidas`. 'ajuste_comercial' NO se toca (la NC resta
  // normal, el documento cuenta normal). Requiere que el `.select` de siiRecibidas traiga
  // `tipo_relacion_nc` y `documento_referenciado_id` (los 4 callers ya lo hacen).
  const {
    facturasAnuladas: anuladasNc,
    notasCredito: ncAnula,
    notasSinRef,
  } = construirExclusionNcAnulaError(siiRecibidas);
  const excluidasAnulaError = new Set([...anuladasNc, ...ncAnula]);

  const debitosSii = siiRecibidas
    .filter((f) => {
      if (f.proveedor_id !== provId || setComisionFactoring.has(f.id)) return false;
      // 'ignorada' (opt-in): iguala el criterio de getFacturasRecibidas(estado_not:'ignorada') del reload.
      if (excluirIgnoradas && f.estado_vinculacion === "ignorada") return false;
      if (excluidasAnulaError.has(f.id)) return false;
      // DIN (Declaración de Ingreso, Aduana, tipo_dte=914): no es deuda de proveedor, se excluye
      // entera del ledger (ni débito ni crédito) — mismo criterio hard-exclusion que anula_error.
      if (esDeclaracionIngreso(f)) return false;
      // Pieza A (regla 10.29): una factura PRE-CORTE (fecha_emision < FECHA_CORTE) solo "despierta"
      // al ledger si tiene residual_corte > 0 (su saldo al 01/01/2026, descompuesto desde el saldo
      // inicial agregado). residual_corte NULL/0 (ej. folio 754 anulado por NC) → sigue DORMIDA: su
      // deuda vive en el saldo inicial. Las NC pre-corte nunca despiertan. Post-corte: entran siempre.
      // Guardrail anti-doble-conteo: el INI se reduce en Σ(residual_corte) más abajo.
      if (f.fecha_emision < FECHA_CORTE) return !esNotaCredito(f) && Number(f.residual_corte) > 0;
      return true;
    })
    .map((f) => {
      // Factura despertada (pre-corte con residual): su débito es el residual_corte (saldo AL corte),
      // NO monto_total → el resto ya se pagó pre-2026 y no debe reaparecer como deuda (anti-doble-conteo).
      const esDespertada = f.fecha_emision < FECHA_CORTE;
      const debitoFactura = esDespertada ? Number(f.residual_corte) : Number(f.monto_total);
      return {
        fecha: f.fecha_emision,
        concepto: esNotaCredito(f) ? "Nota de crédito" : "Factura",
        origen: "SII",
        referencia: f.folio ? String(f.folio) : "-",
        facturaId: f.id,
        // NC (tipo_dte=61) entra como CRÉDITO POSITIVO (no débito negativo): contablemente
        // reduce la deuda y va en su propio bucket "nota_credito". El saldo corrido queda
        // idéntico (Pasada 1: 0 - X == -X - 0). Sigue fuera del FIFO (Pasada 2 exige
        // debitoDisplay>0; Pasada 3 atrapa por origen!==null). Ver regla 10.42.
        debito: esNotaCredito(f) ? 0 : debitoFactura,
        credito: esNotaCredito(f) ? Number(f.monto_total) : 0,
        moneda: monedaPrincipal,
        bucket: esNotaCredito(f) ? "nota_credito" : "factura",
      };
    });

  const debitosExt = facturasExternas
    .filter((f) => f.proveedor_id === provId && f.estado !== "anulada")
    .map((f) => ({
      fecha: f.fecha_emision,
      concepto: f.concepto || null,
      origen: "EXT",
      referencia: f.numero_documento || "-",
      facturaId: f.id,
      debito: Number(f.monto_total),
      credito: 0,
      moneda: f.moneda || monedaPrincipal,
      bucket: "factura",
      archivo_url: f.archivo_url || null,
      periodo_mes: f.periodo_mes || null,
    }));

  // Boletas de honorarios (BHE) como DEUDA del proveedor (Fase 1). Débito = monto_bruto (la BHE es
  // exenta de IVA: el bruto ES el costo, ver normalizarHonorario). Solo las que computan
  // (`computa` = status 'vigente'); las nula/vca no son documento de costo. Origen "BHE", clave
  // composite "BHE:<id>" (misma que documentosCosto.claveDocumento). No tienen ciclo SII → siempre
  // firmes. En moneda local (el SII reporta la BHE en local). Entran a la cola FIFO (Pasada 2) igual
  // que SII/EXT: un pago a cuenta del proveedor las amortiza oldest-first. Filtro `fecha_emision`
  // presente por defensa (el sort del ledger hace localeCompare y null lo rompería).
  const debitosHonorarios = honorarios
    .filter((b) => b.proveedor_id === provId && b.computa && b.fecha_emision)
    .map((b) => ({
      fecha: b.fecha_emision,
      concepto: "Honorario",
      origen: "BHE",
      referencia: b.folio ? String(b.folio) : "-",
      facturaId: b.id,
      debito: Number(b.monto_bruto),
      credito: 0,
      moneda: monedaPrincipal,
      bucket: "factura",
    }));

  // Fase 9b (10.245) — `rendiciones_anticipo`: consumo de un anticipo a ESTE proveedor en gastos
  // SIN documento fiscal propio (ej. derechos/IVA aduanero pagados directo a Aduana) o en un
  // documento de OTRO proveedor pagado por su cuenta (ej. una factura de FedEx que el agente de
  // aduana pagó con nuestro anticipo). Débito igual que una factura (reduce el anticipo/credito
  // recibido): sin esto, un `pago_a_cuenta` grande quedaría mostrando un crédito a favor del
  // proveedor que en realidad ya gastó. `origen: null` (no es SII/EXT/BHE) → nunca entra a
  // `colaFacturas`/`saldosPorFactura` (Pasada 2): no es una "factura" a saldar, solo afecta el
  // saldo corrido. `facturaId` no aplica.
  const debitosRendicion = rendicionesAnticipo
    .filter((r) => r.proveedor_id === provId)
    .map((r) => ({
      fecha: r.fecha,
      concepto: r.concepto,
      origen: null,
      referencia: r.documento_ref || "-",
      debito: Number(r.monto),
      credito: 0,
      moneda: r.moneda || monedaPrincipal,
      bucket: "factura",
    }));

  // Cobros a cuenta corriente del proveedor (devolución, anticipo retornado, refacturación):
  // movimientos tipo='cobro' con entidad_tipo='proveedor'. Aumentan el saldo a favor del
  // proveedor — se contabilizan como débito en el ledger.
  const debitosCobroAcuenta = movimientosCaja
    .filter((m) => m.tipo === "cobro" && m.entidad_tipo === "proveedor" && m.entidad_id === provId)
    .map((m) => ({
      fecha: m.fecha,
      concepto: m.descripcion || "Cobro a cuenta",
      origen: null,
      referencia: m.numero_documento || "-",
      descripcion: m.descripcion || "",
      debito: Number(m.monto),
      credito: 0,
      moneda: m.moneda || monedaPrincipal,
      bucket: "credito",
      // Datos para reclasificar el cobro desde EstadoCuenta (columna TIPO PAGO). El bucket
      // sigue siendo "credito" (cuenta corriente); solo se reclasifica categoria_pago.
      cajaId: m.id,
      categoriaPago: m.categoria_pago,
    }));

  const creditos = movimientosCaja
    .filter((m) => m.tipo === "pago" && m.entidad_tipo === "proveedor" && m.entidad_id === provId)
    .map((m) => ({
      fecha: m.fecha,
      concepto: m.categoria_pago === "pago_a_cuenta" ? "Pago a cuenta" : "Pago",
      origen: null,
      referencia: m.numero_documento || "-",
      descripcion: m.descripcion || "",
      debito: 0,
      credito: Number(m.monto),
      moneda: m.moneda || monedaPrincipal,
      bucket: m.categoria_pago === "pago_credito" || m.categoria_pago === "pago_a_cuenta" ? "credito" : "factura",
      // Para detectar pagos con vinculación directa a factura (MBF) y excluirlos del FIFO.
      movimientoBancarioId: m.movimiento_bancario_id ?? null,
      // Datos para que EstadoCuenta pueda accionar sobre el pago (desvincular/eliminar).
      cajaId: m.id,
      categoriaPago: m.categoria_pago,
      tieneMBF: false, // se sobreescribe abajo si el pago tiene fila MBF
    }));

  // Pieza A: el saldo inicial AGREGADO se descompone por folio. Lo ya "despertado" como facturas
  // pre-corte con residual_corte se resta del INI para no doble-contar:
  //   INI_residual = saldos_iniciales − Σ(residual_corte de las facturas despertadas de esta entidad).
  // Guardrail aritmético: INI_residual + Σ(residual_corte) = saldos_iniciales → total por entidad
  // IDÉNTICO haya o no descomposición. Blackim (descompuesto full): Σ = saldo inicial → INI_residual = 0
  // → sin entry INI. Entidades sin residual_corte: Σ = 0 → INI_residual = saldo inicial (comportamiento
  // previo intacto). El criterio de "despertada" es el MISMO que el filtro de debitosSii (consistencia).
  const sumaResidualCorte = siiRecibidas
    .filter(
      (f) =>
        f.proveedor_id === provId &&
        !setComisionFactoring.has(f.id) &&
        !(excluirIgnoradas && f.estado_vinculacion === "ignorada") &&
        !excluidasAnulaError.has(f.id) &&
        !esNotaCredito(f) &&
        !esDeclaracionIngreso(f) &&
        f.fecha_emision < FECHA_CORTE &&
        Number(f.residual_corte) > 0
    )
    .reduce((acc, f) => acc + Number(f.residual_corte), 0);
  const iniResidualMonto = saldoInicial ? Number(saldoInicial.monto || 0) - sumaResidualCorte : 0;
  const inicial =
    saldoInicial && Math.abs(iniResidualMonto) > 0.01
      ? [
          {
            ...buildSaldoInicialEntry({ ...saldoInicial, monto: iniResidualMonto }, monedaPrincipal),
            bucket: "credito",
          },
        ]
      : [];
  // Fase 9b — renglón INFORMATIVO: una factura de ESTE proveedor saldada en el libro por un
  // anticipo de OTRO proveedor (ej. FedEx 189, pagado por el agente de aduana 126 con su propio
  // anticipo). Sin esto, la cuenta corriente de 189 mostraría deuda pese a que el documento ya
  // está resuelto (Fase 5). Puramente derivado del evento REAL de `aplicaciones_saldo` (vía
  // `ctxLibro`, ya recibido por el consumidor) — no escribe nada, no es un movimiento de caja.
  //
  // Acotado a `via==='anticipo'` (el único caso real hoy: FedEx 33681 + precedente 4576) —
  // 'nota_credito' queda afuera a propósito: esa NC ya tiene su propia entry en `debitosSii`
  // (bucket "nota_credito", crédito=monto_total) y agregar esto la duplicaría. 'factoring'/
  // 'corte_historico' quedan fuera de alcance (sin caso real hoy).
  //
  // Guard CRÍTICO (verificado con datos reales, caso 4576/Visiona): si el `movimiento_caja` que
  // originó el anticipo (`origen_id`) pertenece al MISMO proveedor (`entidad_id === provId`), esa
  // plata YA es un crédito real en `creditos`/`debitosCobroAcuenta` de este mismo proveedor (su
  // propio pago/cobro a cuenta) — agregar la sintética DUPLICARÍA el crédito. Precedente 4576
  // (proveedor 140, caja 748 con entidad_id=140): el guard excluye este caso, su cuenta queda
  // EXACTAMENTE igual que antes. Solo aplica cuando el origen es de un proveedor DISTINTO.
  const creditosAnticipoAjeno = [];
  if (ctxLibro) {
    for (const f of debitosSii) {
      if (f.facturaId == null || f.bucket === "nota_credito") continue;
      const eventos = ctxLibro.porDocumento?.get(`sii_recibida:${f.facturaId}`) || [];
      for (const ev of eventos) {
        if (ev.via !== "anticipo" || ev.origen_id == null) continue;
        const cajaOrigen = movimientosCaja.find((m) => m.id === ev.origen_id);
        if (!cajaOrigen || cajaOrigen.entidad_tipo !== "proveedor" || cajaOrigen.entidad_id === provId) continue;
        creditosAnticipoAjeno.push({
          fecha: ev.fecha_evento,
          concepto: `Saldada vía anticipo de ${cajaOrigen.entidad_nombre || "otro proveedor"}`,
          origen: null,
          referencia: f.referencia,
          debito: 0,
          credito: Number(ev.monto_documento),
          moneda: monedaPrincipal,
          bucket: "factura",
          informativo: true, // EstadoCuenta: no es un movimiento real de caja, solo derivado del libro.
        });
      }
    }
  }

  const entries = [
    ...inicial,
    ...debitosSii,
    ...debitosExt,
    ...debitosHonorarios,
    ...debitosRendicion,
    ...debitosCobroAcuenta,
    ...creditos,
    ...creditosAnticipoAjeno,
  ].sort((a, b) => a.fecha.localeCompare(b.fecha));

  // Vinculaciones directas (MBF) con montos parciales: una factura puede ser saldada por N
  // movimientos bancarios (caso Santander: débito CLP + débito USD contra la misma factura).
  // Para cada factura se acumula el monto vinculado EN MONEDA DISPLAY — cada mbf.monto está en
  // la moneda nativa de su movimiento (Fase 1 guarda Math.abs(movimiento.monto)), así que se
  // convierte con la moneda/fecha del pago. Si Σ vinculado ≥ débito → saldada al 100% (fuera
  // de "Por pagar"); si es parcial → solo el remanente entra a la cola FIFO. El crédito de cada
  // pago se fuerza a su mbf.monto (no al total de la factura) para que el split acredite el
  // parcial real. Las keys son composite "ORIGEN:facturaId"; MBF mapea sii_recibida→SII,
  // factura_externa→EXT (sii_emitida es de clientes, no aplica al ledger de proveedor).
  const montosVinculadosPorFactura = new Map(); // "ORIGEN:facturaId" → Σ mbf.monto (en display)
  const montoPorPagoMBF = new Map(); // movimiento_bancario_id → Σ mbf.monto del pago (moneda nativa)
  const pagosConsumidosPorMBF = new Set();
  // Para el neteo exacto del drift cambiario (Pasada 1): pago → facturas que cubre, cuántos pagos
  // distintos tocan cada factura (para excluir el split N-movimientos→1-factura) y el débito display
  // de cada factura.
  const facturasPorPago = new Map(); // movimiento_bancario_id → Set de "ORIGEN:facturaId"
  const pagosPorFacturaSet = new Map(); // "ORIGEN:facturaId" → Set de movimiento_bancario_id
  const debitoDisplayPorFactura = new Map(); // "ORIGEN:facturaId" → debitoDisplay
  const monedaPorFactura = new Map(); // "ORIGEN:facturaId" → moneda nativa de la factura

  // Keys de facturas de ESTE proveedor (para ignorar filas MBF que apunten a facturas de otro
  // proveedor o a sii_emitida — mismo criterio defensivo que el código previo). De paso se guarda
  // el débito display y la moneda de cada factura (reusados por el neteo exacto de la Pasada 1).
  const facturaKeysProveedor = new Set();
  for (const e of [...debitosSii, ...debitosExt, ...debitosHonorarios]) {
    if (e.facturaId != null && e.debito > 0) {
      const key = `${e.origen}:${e.facturaId}`;
      facturaKeysProveedor.add(key);
      debitoDisplayPorFactura.set(key, toDisplayCurrency(e.debito, e.moneda, e.fecha));
      monedaPorFactura.set(key, e.moneda);
    }
  }
  // Moneda + fecha de cada pago bancario del proveedor (para convertir su mbf.monto a display
  // de forma consistente entre el lado factura y el lado pago).
  const infoPagoPorMov = new Map();
  for (const c of creditos) {
    if (c.movimientoBancarioId != null)
      infoPagoPorMov.set(c.movimientoBancarioId, { moneda: c.moneda, fecha: c.fecha });
  }
  for (const mbf of vinculacionesDirectas) {
    // boleta_honorario→BHE (F2b): un pago atado por MBF a una boleta la salda igual que a una SII.
    // El pago igual entra a pagosConsumidosPorMBF abajo → se excluye del FIFO (anti-doble-conteo).
    const origenKey =
      mbf.factura_tipo === "sii_recibida"
        ? "SII"
        : mbf.factura_tipo === "factura_externa"
          ? "EXT"
          : mbf.factura_tipo === "boleta_honorario"
            ? "BHE"
            : null;
    if (origenKey && mbf.factura_id != null) {
      const key = `${origenKey}:${mbf.factura_id}`;
      if (facturaKeysProveedor.has(key)) {
        const info = infoPagoPorMov.get(mbf.movimiento_bancario_id);
        const montoDisplay = toDisplayCurrency(
          Number(mbf.monto || 0),
          info?.moneda || monedaPrincipal,
          info?.fecha || null
        );
        montosVinculadosPorFactura.set(key, (montosVinculadosPorFactura.get(key) || 0) + montoDisplay);
        if (mbf.movimiento_bancario_id != null) {
          montoPorPagoMBF.set(
            mbf.movimiento_bancario_id,
            (montoPorPagoMBF.get(mbf.movimiento_bancario_id) || 0) + Number(mbf.monto || 0)
          );
          if (!facturasPorPago.has(mbf.movimiento_bancario_id))
            facturasPorPago.set(mbf.movimiento_bancario_id, new Set());
          facturasPorPago.get(mbf.movimiento_bancario_id).add(key);
          if (!pagosPorFacturaSet.has(key)) pagosPorFacturaSet.set(key, new Set());
          pagosPorFacturaSet.get(key).add(mbf.movimiento_bancario_id);
        }
      }
    }
    if (mbf.movimiento_bancario_id != null) {
      pagosConsumidosPorMBF.add(mbf.movimiento_bancario_id);
    }
  }

  // Pasada 1 — enrich con saldo acumulado. Sin lógica FIFO adentro. Para un pago con
  // vinculación directa (MBF), el creditoDisplay se fuerza a la suma de los debitoDisplay de
  // las facturas que cancela, de modo que se anulen exacto contra los débitos de esas facturas.
  let saldo = 0;
  const entriesConSaldo = entries.map((entry) => {
    const debitoDisplay = toDisplayCurrency(entry.debito, entry.moneda, entry.fecha);
    let creditoDisplay = toDisplayCurrency(entry.credito, entry.moneda, entry.fecha);
    if (
      entry.origen === null &&
      entry.credito > 0 &&
      entry.movimientoBancarioId != null &&
      pagosConsumidosPorMBF.has(entry.movimientoBancarioId)
    ) {
      // Neteo exacto del drift cambiario. Un pago es "coverer exacto" si TODAS las facturas que
      // toca cumplen: (a) es CROSS-CURRENCY (moneda del pago ≠ moneda de la factura) — el drift solo
      // existe al convertir de una moneda a otra; (b) es el ÚNICO pago que las cubre (no split
      // N-movimientos→1-factura); (c) están 100% cubiertas. En ese caso el crédito se fuerza a la Σ
      // de los débitos display de esas facturas → débito y crédito se anulan EXACTO, sin arrastrar la
      // diferencia de TC (CLP→USD con Math.round). Si no, se mantiene la conversión del pago
      // (Σ mbf.monto convertido) para que el remanente/diferencia real siga contando. El gate (a)
      // garantiza que en MISMA moneda (OnSign USD, proveedor CLP) es NO-OP: no se toca ningún
      // sobrepago/diferencia same-currency real.
      const mbfMonto = montoPorPagoMBF.get(entry.movimientoBancarioId);
      if (mbfMonto != null) {
        const facturasCubiertas = facturasPorPago.get(entry.movimientoBancarioId);
        let covererExacto = facturasCubiertas != null && facturasCubiertas.size > 0;
        let sumaDebitoCubierto = 0;
        if (covererExacto) {
          for (const fkey of facturasCubiertas) {
            const debFactura = debitoDisplayPorFactura.get(fkey) || 0;
            const esCrossCurrency = monedaPorFactura.get(fkey) !== entry.moneda;
            const unicoPago = (pagosPorFacturaSet.get(fkey)?.size || 0) === 1;
            const cubierta100 = (montosVinculadosPorFactura.get(fkey) || 0) >= debFactura - 0.01;
            if (!esCrossCurrency || !unicoPago || !cubierta100) {
              covererExacto = false;
              break;
            }
            sumaDebitoCubierto += debFactura;
          }
        }
        creditoDisplay = covererExacto ? sumaDebitoCubierto : toDisplayCurrency(mbfMonto, entry.moneda, entry.fecha);
      }
    }
    saldo += debitoDisplay - creditoDisplay;
    return { ...entry, debitoDisplay, creditoDisplay, saldo };
  });

  // Pasada 2 — registrar TODAS las facturas SII/EXT primero (ASC por fecha por el
  // sort previo). Separarlo de la aplicación FIFO evita el bug del cursor cuando un
  // pago ocurre cronológicamente ANTES de algunas facturas: en single-pass `fifoIdx`
  // avanzaría sobre una cola incompleta y las facturas posteriores quedarían sin
  // recibir crédito remanente. Composite key porque SII/EXT tienen IDs
  // independientes que pueden colisionar dentro del mismo proveedor.
  const saldosPorFactura = new Map();
  const colaFacturas = [];
  // Contadores para decidir si TODA la deuda del proveedor está cubierta por MBF (→ residuo 0).
  let totalFacturasProveedor = 0;
  let facturasExcluidasMBF = 0;
  for (const entry of entriesConSaldo) {
    const esFactura =
      (entry.origen === "SII" || entry.origen === "EXT" || entry.origen === "BHE") &&
      entry.debitoDisplay > 0 &&
      entry.facturaId != null;
    // Saldo inicial (origen "INI", débito > 0) entra a la cola como la obligación MÁS ANTIGUA
    // (fecha = FECHA_CORTE, primera del sort) → los pagos/NCs amortizan primero la deuda
    // pre-2026 y las facturas 2026 conservan su saldo real (evita residuo fantasma). No es
    // "factura" (no cuenta para totalFacturasProveedor) y nunca tiene MBF.
    const esSaldoIni = entry.origen === "INI" && entry.debitoDisplay > 0;
    if (!esFactura && !esSaldoIni) continue;
    const key = esSaldoIni ? `INI:${provId}` : `${entry.origen}:${entry.facturaId}`;
    if (esFactura) {
      totalFacturasProveedor++;
      // Vinculación directa por montos (MBF): comparar la suma vinculada (en display) contra el
      // débito de la factura. Cubre el total → saldada al 100% (fuera de "Por pagar", pago(s)
      // excluido(s) del FIFO en la Pasada 3). Parcial → solo el remanente entra a la cola FIFO.
      // Soporta N movimientos → 1 factura (incluido el split cross-currency CLP + USD).
      const montoVinculadoDisplay = montosVinculadosPorFactura.get(key) || 0;
      if (montoVinculadoDisplay > 0.01) {
        if (montoVinculadoDisplay >= entry.debitoDisplay - 0.01) {
          facturasExcluidasMBF++;
          continue;
        }
        saldosPorFactura.set(key, { saldoDisplay: entry.debitoDisplay - montoVinculadoDisplay, factura: entry });
        colaFacturas.push(key);
        continue;
      }
    }
    saldosPorFactura.set(key, { saldoDisplay: entry.debitoDisplay, factura: entry });
    colaFacturas.push(key);
  }

  // Pasada 3 — aplicar reducciones FIFO sobre la cola completa (oldest-first):
  //   (a) PAGOS al proveedor: origen=null + creditoDisplay>0 + bucket!="credito".
  //   (b) NOTAS DE CRÉDITO: bucket="nota_credito" + creditoDisplay>0 — reducen la deuda
  //       contra las obligaciones más antiguas igual que un pago. Antes quedaban FUERA del
  //       FIFO (solo bajaban el saldo global) y generaban residuo fantasma; ahora se
  //       distribuyen sobre la cola (incluido el saldo inicial, que es el ítem más antiguo).
  // NO participan: cobros a cuenta (origen=null + debito>0) ni pagos bucket crédito
  // (pago_credito/pago_a_cuenta) — netean contra sus pares en el saldo global. El saldo
  // inicial participa como ITEM de la cola (Pasada 2), no como reducción.
  let fifoIdx = 0;
  for (const entry of entriesConSaldo) {
    const esPagoFifo = entry.origen === null && entry.creditoDisplay > 0.01 && entry.bucket !== "credito";
    const esNotaCredito = entry.bucket === "nota_credito" && entry.creditoDisplay > 0.01;
    if (!esPagoFifo && !esNotaCredito) continue;
    // Pago con vinculación directa (MBF): ya saldó su(s) factura(s) explícitamente, no se
    // reparte por FIFO (si no, comería saldo de otras facturas y dejaría residuos fantasma).
    if (esPagoFifo && entry.movimientoBancarioId != null && pagosConsumidosPorMBF.has(entry.movimientoBancarioId))
      continue;
    let restante = entry.creditoDisplay;
    while (restante > 0.01 && fifoIdx < colaFacturas.length) {
      const item = saldosPorFactura.get(colaFacturas[fifoIdx]);
      if (!item || item.saldoDisplay <= 0.01) {
        fifoIdx++;
        continue;
      }
      const aplicar = Math.min(restante, item.saldoDisplay);
      item.saldoDisplay -= aplicar;
      restante -= aplicar;
      if (item.saldoDisplay <= 0.01) fifoIdx++;
    }
  }

  // Descartar facturas saldadas: exacto (saldoDisplay ≤ 0.01) o con una miga de redondeo TRAS
  // pago (saldoDisplay ≤ tolerancia y débito − saldo > 0). tolLedger expresa TOLERANCIA_REDONDEO_LOCAL
  // en la moneda del ledger: ~10 en ledger local, ~0 (inerte) en ledger USD. Ver nota del constante.
  const tolLedger = toDisplayCurrency(TOLERANCIA_REDONDEO_LOCAL, monedaPrincipal, null);
  for (const [key, item] of saldosPorFactura) {
    const debitoItem = item.factura?.debitoDisplay ?? 0;
    const huboPago = debitoItem - item.saldoDisplay > 0.01; // se aplicó pago/NC/MBF a esta factura
    const migaTrasPago = huboPago && item.saldoDisplay <= tolLedger;
    if (item.saldoDisplay <= 0.01 || migaTrasPago) saldosPorFactura.delete(key);
  }

  // El saldo inicial NO es una factura: aunque participó del FIFO (Pasada 2/3) para amortizar
  // primero la deuda pre-corte, su residual (si los pagos/NCs no la cubrieron) NO debe figurar
  // como fila de factura en "Por pagar". Se quita del map → su residual fluye al RESIDUO vía
  // (saldoFinal − totalPorFactura), que es donde corresponde la deuda pre-2026 no atribuible.
  saldosPorFactura.delete(`INI:${provId}`);

  // Residuo: deuda global del ledger no atribuible a ninguna factura específica.
  // Aparece cuando hay cobros a cuenta / saldo inicial que suben el saldo global
  // pero el FIFO no los aplica a facturas. Se emite como entry sintética con key
  // "RESIDUO" para que el consumidor pueda mostrarlo como "Saldo a cuenta".
  // El residuo SOLO se emite con saldo final POSITIVO (le debemos): "Por pagar" es deuda
  // pendiente. Un saldo ≤ 0 significa que ya se pagó todo (o de más — ej. pagos sin factura
  // que los respalde) → NO es deuda futura, no se emite. Antes se usaba `Math.abs(saldoFinal)`,
  // que volteaba esos pagos-sin-factura a un fantasma positivo fechado hoy en "Por pagar".
  //
  // Excepción: si TODAS las facturas del proveedor están cubiertas por vinculación directa
  // (MBF), no hay deuda real — la diferencia del saldo global es puramente cambiaria (pagos
  // locales convertidos a moneda extranjera no dan exacto). En ese caso el residuo se fuerza
  // a 0 para no proyectar esa diferencia de TC como "Saldo a cuenta" pendiente. El saldo
  // global (entries) NO se toca: sigue reflejando la diferencia cambiaria en EstadoCuenta.
  const todasCubiertasPorMBF = totalFacturasProveedor > 0 && facturasExcluidasMBF === totalFacturasProveedor;
  const totalPorFactura = [...saldosPorFactura.values()].reduce((acc, item) => acc + item.saldoDisplay, 0);
  const saldoFinal = entriesConSaldo.length > 0 ? entriesConSaldo.at(-1).saldo : 0;
  const residuo =
    todasCubiertasPorMBF || saldoFinal <= 0.01 ? 0 : Math.round((saldoFinal - totalPorFactura) * 100) / 100;
  if (residuo > 0.01) {
    saldosPorFactura.set("RESIDUO", {
      saldoDisplay: residuo,
      factura: {
        origen: "RESIDUO",
        concepto: "Saldo a cuenta",
        referencia: null,
        fecha: new Date().toISOString().slice(0, 10),
      },
    });
  }

  // Enriquecimiento (no afecta el cálculo): marcar entries de pago cuyo movimiento bancario
  // tiene fila MBF. EstadoCuenta lo usa para decidir Desvincular (Sección A, libera factura)
  // vs Eliminar (Sección B, solo caja+banco). Aditivo — ver pendiente 9.9.
  const mbIdsConMBF = new Set((vinculacionesDirectas || []).map((v) => v.movimiento_bancario_id));
  entriesConSaldo.forEach((e) => {
    if (e.movimientoBancarioId && mbIdsConMBF.has(e.movimientoBancarioId)) {
      e.tieneMBF = true;
    }
  });

  // ─── R3.2 — la SALDADA/SALDO por factura sale del LIBRO (resolverSaldo), no del FIFO ────────────
  // Cutover del eje pago (10.190). Cuando el consumidor pasa `ctxLibro`, la saldada/saldo POR FACTURA de
  // las **recibidas SII** y **boletas (BHE)** la determina `resolverSaldo` sobre `aplicaciones_saldo`.
  //
  // Las **facturas EXTERNAS (EXT) siguen con el saldo FIFO** — NO se re-derivan del libro. Su cutover al
  // libro es R3.4 (gate de externas). Además, la externa es el caso de la decisión C1 (Ignacio Mailhos,
  // proveedor recurrente): sus pagos fueron transferencias/giros genéricos nunca atribuidos documento-a-
  // documento, así que el libro (per-documento) las daba pendientes mientras el FIFO (cuenta corriente)
  // las da saldadas — divergencia ACEPTADA. Manteniéndolas en FIFO acá, el badge "por pagar" (que sale
  // de este map) queda consistente con la tabla/saldo-corrido del ledger (que también es FIFO): la
  // cuenta se ve saldada, no se reabre. RESIDUO e INI (saldo a cuenta / saldo inicial) tampoco cambian.
  //
  // `entriesConSaldo` ya trae SOLO las facturas-deuda (NC = débito 0; anula_error/ignorada/comisión-
  // factoring ya excluidas en los débitos), así que iterar sus entries respeta las mismas exclusiones
  // que el FIFO (10.223).
  if (ctxLibro) {
    const totalRawSii = new Map(siiRecibidas.map((f) => [f.id, Number(f.monto_total || 0)]));
    // Descartar del map SOLO las filas SII/BHE del FIFO (se re-derivan del libro abajo); conservar
    // RESIDUO, INI y **EXT** (las externas mantienen su saldo FIFO). Se reconstruye sin borrar-durante-
    // iteración: se copian los preservados, se limpia y se restauran.
    const preservados = new Map();
    for (const [key, val] of saldosPorFactura) {
      if (key === "RESIDUO" || key.startsWith("INI:") || key.startsWith("EXT:")) preservados.set(key, val);
    }
    saldosPorFactura.clear();
    for (const [key, val] of preservados) saldosPorFactura.set(key, val);

    // Pasada 1: resolver el saldo POR FACTURA desde el libro (SII/BHE; EXT excluida a propósito, sigue
    // FIFO — cutover R3.4). Se junta el saldo pendiente EN MONEDA DEL DOCUMENTO para poder netear abajo
    // el crédito de las NC `anula_error` sin ref antes de convertir a display.
    const pendientesLibro = []; // { entry, tipo, saldoDoc } — facturas con saldo>0 según el libro
    for (const entry of entriesConSaldo) {
      const esFactura =
        (entry.origen === "SII" || entry.origen === "BHE") && entry.facturaId != null && entry.debito > 0;
      if (!esFactura) continue;
      const tipo = entry.origen === "SII" ? "sii_recibida" : "boleta_honorario";
      // SII pre-corte despertada: el débito del ledger es `residual_corte`; `resolverSaldo` usa
      // `monto_total` + el seed `corte_historico` (que representa lo pre-pagado). BHE: el débito ES el
      // total. `entry.moneda` es la moneda del documento (CLP para recibida/boleta).
      const total = entry.origen === "SII" ? (totalRawSii.get(entry.facturaId) ?? entry.debito) : entry.debito;
      const rs = resolverSaldo({ tipo, id: entry.facturaId, monto_total: total, moneda: entry.moneda }, ctxLibro);
      if (rs.saldada) continue; // saldada por el libro → fuera de "Por pagar"
      const saldoDoc = Number(rs.saldo || 0);
      if (saldoDoc > 0.01) pendientesLibro.push({ entry, saldoDoc });
    }

    // Pasada 2: netear el crédito genérico de las NC `anula_error` SIN ref (regla 10.225, lado Caja)
    // contra las facturas SII pendientes, oldest-first — mismo criterio que el FIFO, como regla
    // explícita del libro (`netearNcSinRefPago`). Sin esto, la factura anulada por una NC no vinculable
    // (Contadores 68, Entel 23) reaparecía como pendiente fantasma bajo R3.2. Solo aplica a SII (las NC
    // son `sii_recibida`); BHE no tiene NC de este tipo.
    const facturasPendientesSii = pendientesLibro
      .filter((p) => p.entry.origen === "SII")
      .map((p) => ({
        id: p.entry.facturaId,
        proveedor_id: provId,
        fecha_emision: p.entry.fecha,
        saldoPendiente: p.saldoDoc,
      }));
    const absorbidoPorNc = netearNcSinRefPago({ notasSinRef, facturasPendientes: facturasPendientesSii });

    // Pasada 3: emitir el saldo (ya neto) convertido a display. Una factura absorbida al 100% por la NC
    // queda en ~0 y sale de "por pagar"; una reducción parcial (no esperada en anula_error total) deja
    // el remanente visible (nunca esconde deuda por encima del monto de la NC).
    for (const { entry, saldoDoc } of pendientesLibro) {
      const neto = entry.origen === "SII" ? saldoDoc - (absorbidoPorNc.get(entry.facturaId) || 0) : saldoDoc;
      if (neto <= 0.01) continue;
      const saldoDisplay = toDisplayCurrency(neto, entry.moneda, entry.fecha);
      if (saldoDisplay > 0.01) {
        saldosPorFactura.set(`${entry.origen}:${entry.facturaId}`, { saldoDisplay, factura: entry });
      }
    }
  }

  return { entries: entriesConSaldo.reverse(), saldosPorFactura };
};
